import { FormattedMessage } from 'react-intl';
import OverlayTrigger from '@rio-cloud/rio-uikit/lib/es/OverlayTrigger';
import Popover from '@rio-cloud/rio-uikit/lib/es/Popover';

import { SidebarStatusBar } from '@/features/diagnostics/sidebar/SidebarStatusBar';
import { BrakeLiningsMeasurement, Criticality } from '@/generated/fleetAggregationServiceTypes';
import { FeatureToggle, isFeatureToggleEnabled } from '@/hooks/useFeatureToggle';
import useHoverIntent from '@/hooks/useHoverIntent';
import analyticsEvents from '@/utils/analytics/AnalyticsEvents';
import { getCriticalityClassTextColor, isDangerOrWarning } from '@/utils/criticalityMappings';

type RecommendationPopoverContent = {
    [key in Criticality]?: {
        icon: string;
        color: string;
        titleKey: string;
        messageKey: string;
    };
};

const RECOMMENDATION_POPOVER_CONTENT: RecommendationPopoverContent = {
    warning: {
        icon: 'rioglyph-warning-sign',
        color: 'text-color-warning',
        titleKey: 'intl-msg:fleetstatus.diagnostics.sidebar.axle_info.recommendation.warning.title',
        messageKey: 'intl-msg:fleetstatus.diagnostics.sidebar.axle_info.recommendation.warning.description',
    },
    danger: {
        icon: 'rioglyph-error-sign',
        color: 'text-color-danger',
        titleKey: 'intl-msg:fleetstatus.diagnostics.sidebar.axle_info.recommendation.danger.title',
        messageKey: 'intl-msg:fleetstatus.diagnostics.sidebar.axle_info.recommendation.danger.description',
    },
};

const BRAKE_LININGS_RECOMMENDATION_POPOVER_CONTENT: RecommendationPopoverContent = {
    warning: {
        icon: 'rioglyph-warning-sign',
        color: 'text-color-warning',
        titleKey: 'intl-msg:fleetstatus.diagnostics.sidebar.axle_info.recommendation.service_soon.title',
        messageKey: 'intl-msg:fleetstatus.diagnostics.sidebar.axle_info.recommendation.service_soon.description',
    },
    danger: {
        icon: 'rioglyph-error-sign',
        color: 'text-color-danger',
        titleKey: 'intl-msg:fleetstatus.diagnostics.sidebar.axle_info.recommendation.schedule_service.title',
        messageKey: 'intl-msg:fleetstatus.diagnostics.sidebar.axle_info.recommendation.schedule_service.description',
    },
};

export const BRAKE_LININGS_STATUS_MESSAGE_IDS: Partial<Record<Criticality, string>> = {
    danger: 'intl-msg:fleetstatus.diagnostics.sidebar.section.brake_linings.schedule_service',
    warning: 'intl-msg:fleetstatus.diagnostics.sidebar.section.brake_linings.service_soon',
};

const getStatusMessageId = (criticality: Criticality, isServiceCareLAlphaEnabled: boolean) => {
    const defaultStatusMessageId = `intl-msg:fleetstatus.diagnostics.criticality.${criticality}`;

    if (!isServiceCareLAlphaEnabled) {
        return defaultStatusMessageId;
    }

    return BRAKE_LININGS_STATUS_MESSAGE_IDS[criticality] ?? defaultStatusMessageId;
};

type BrakeLiningsStatusProps = {
    brakeLiningMeasurement: BrakeLiningsMeasurement;
};

const BrakeLiningsStatus = ({ brakeLiningMeasurement: { percentage, criticality } }: BrakeLiningsStatusProps) => {
    const isServiceCareLAlphaEnabled = isFeatureToggleEnabled(FeatureToggle.ENABLE_SERVICE_CARE_L_ALPHA);
    const recommendationPopoverContent = isServiceCareLAlphaEnabled ? BRAKE_LININGS_RECOMMENDATION_POPOVER_CONTENT : RECOMMENDATION_POPOVER_CONTENT;
    const config = recommendationPopoverContent[criticality];
    const statusMessageId = getStatusMessageId(criticality, isServiceCareLAlphaEnabled);
    const [hoverRef] = useHoverIntent(1000, () => {
        analyticsEvents.hoverBrakeLiningsInfo();
    });

    if (percentage === undefined) {
        const noPercentageClassName = [getCriticalityClassTextColor(criticality), criticality !== 'unknown' && 'text-uppercase', isDangerOrWarning(criticality) && 'text-bold']
            .filter(Boolean)
            .join(' ');

        return (
            <td className="display-flex justify-content-center align-items-center border-none text-center">
                <span className={noPercentageClassName}>
                    <FormattedMessage id={statusMessageId} />
                </span>
            </td>
        );
    } else {
        return (
            <>
                <td className="border-none" style={{ minWidth: '35px' }}>
                    {percentage}%
                </td>
                <td className="width-100pct border-none">
                    <SidebarStatusBar percentage={percentage} criticality={criticality} />
                </td>
                <td className="border-none" style={{ minWidth: '40px' }}>
                    {isDangerOrWarning(criticality) ? (
                        <OverlayTrigger
                            placement="bottom"
                            overlay={
                                <Popover
                                    title={
                                        <div className={'display-flex align-items-start gap-5'}>
                                            <span className={`rioglyph ${config?.icon} ${config?.color} text-size-16 width-auto`} />
                                            <span className="text-color-darkest text-bold">
                                                <FormattedMessage id={config?.titleKey} />
                                            </span>
                                        </div>
                                    }
                                    className="top-10 max-width-250"
                                >
                                    <FormattedMessage id={config?.messageKey} />
                                </Popover>
                            }
                        >
                            <span ref={hoverRef} data-testid="brake-linings-recommendation-icon" className="rioglyph rioglyph-info-sign text-color-dark text-size-16" />
                        </OverlayTrigger>
                    ) : null}
                </td>
            </>
        );
    }
};

export default BrakeLiningsStatus;
